import { Draggable, Droppable } from '@hello-pangea/dnd';
import { GripVertical } from 'lucide-react';
import { useState } from 'react';
import styled from 'styled-components';

import { FreefoodIcon } from 'components/FreefoodIcon';
import { UNASSIGNED_COLOR } from 'pages/DynamicDeliveryPlanning/constants/approColors';

import {
    deliveryAddressLabel,
    deliveryContractLabel,
    stopCoordinates,
    deliveryFullLabel,
    deliveryWindow,
    deliveryZoneName,
    formatWindow,
    getStopState,
    hasFreefood,
    type TourDelivery,
    type TourTask,
} from '../helpers';
import { OriginIcon } from './OriginIcon';
import { LOOSE_DROPPABLE_ID, TaskStopContent } from './RoundSection';
import { STATE_TONE, StatePill, WindowChip } from './StopStatus';
import { DELIVERY_STATES_LABELS } from 'pages/Deliveries/constants/states';

type Props = {
    /** Deliveries in no tour yet — draggable into a tour card's drop zone. */
    deliveries: TourDelivery[];
    tasks: TourTask[];
    onOpenTask?: (task: TourTask) => void;
    /** Optional line under the header (e.g. "sélectionnez une tournée pour y glisser un arrêt"). */
    note?: string;
    /** The chip opened for details — the map shows that delivery (pan + ringed marker). */
    onOpen?: (stopId: string | null) => void;
    selectionNumberByDeliveryId?: Map<string, number>;
    onToggleDelivery?: (deliveryId: string) => void;
};

/**
 * The « À affecter » pool: deliveries without a tour, shown as draggable chips. Dropping one onto a tour
 * card moves it there; dropping a tour's delivery HERE releases it (both via the updateTour snapshot).
 */
export const LoosePanel = ({
    deliveries,
    tasks,
    onOpenTask,
    note,
    onOpen,
    selectionNumberByDeliveryId,
    onToggleDelivery,
}: Props) => {
    /** Chip opened to show where the delivery actually is — a loose delivery is only placeable once you know that. */
    const [openedId, setOpenedId] = useState<string | null>(null);

    // Same numbering as the grey « À affecter » markers on the map, which only plots the geolocated stops.
    const mapNumberById = new Map(
        [...deliveries, ...tasks]
            .filter((stop) => stopCoordinates(stop) != null)
            .map((stop, index) => [stop._id, index + 1]),
    );

    return (
        <Panel>
            <Head>À affecter · {deliveries.length + tasks.length}</Head>
            {note && <Note>{note}</Note>}
            <Droppable droppableId={LOOSE_DROPPABLE_ID}>
                {(dropProvided, dropSnapshot) => (
                    <List
                        ref={dropProvided.innerRef}
                        {...dropProvided.droppableProps}
                        $over={dropSnapshot.isDraggingOver}
                    >
                        {deliveries.map((delivery, index) => {
                            const coordinates = stopCoordinates(delivery);
                            const { start, stop } = deliveryWindow(delivery);
                            return (
                                <Draggable key={delivery._id} draggableId={delivery._id} index={index}>
                                    {(dragProvided, dragSnapshot) => (
                                        <ChipBlock
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            {...dragProvided.dragHandleProps}
                                            $dragging={dragSnapshot.isDragging}
                                            $pending={selectionNumberByDeliveryId?.has(delivery._id) ?? false}
                                        >
                                            <ChipRow
                                                onClick={() => {
                                                    if (onToggleDelivery) {
                                                        onToggleDelivery(delivery._id);
                                                        return;
                                                    }
                                                    const next = openedId === delivery._id ? null : delivery._id;
                                                    setOpenedId(next);
                                                    onOpen?.(next);
                                                }}
                                            >
                                                <GripVertical size={13} />
                                                <MapNumber title="Numéro du point sur la carte">
                                                    {selectionNumberByDeliveryId?.get(delivery._id) ??
                                                        mapNumberById.get(delivery._id) ??
                                                        '·'}
                                                </MapNumber>
                                                <ChipName title={deliveryFullLabel(delivery)}>
                                                    {deliveryFullLabel(delivery)}
                                                </ChipName>
                                                {hasFreefood(delivery) && <FreefoodIcon size={13} />}
                                                <OriginIcon
                                                    origin={delivery.isDynamic ? 'dynamique' : 'fixe'}
                                                    size={13}
                                                />
                                                {delivery.deliveredMicrostoreColumnIds.length > 0 && (
                                                    <span>{delivery.deliveredMicrostoreColumnIds.length} col.</span>
                                                )}
                                                <WindowChip $muted={!start && !stop}>
                                                    {formatWindow(start, stop)}
                                                </WindowChip>
                                                <StatePill $tone={STATE_TONE[getStopState(delivery)] ?? 'neutral'}>
                                                    {DELIVERY_STATES_LABELS[getStopState(delivery)]}
                                                </StatePill>
                                            </ChipRow>
                                            {openedId === delivery._id && (
                                                <ChipDetails>
                                                    <div>{deliveryAddressLabel(delivery)}</div>
                                                    {deliveryZoneName(delivery) && (
                                                        <div>Zone {deliveryZoneName(delivery)}</div>
                                                    )}
                                                    {deliveryContractLabel(delivery) && (
                                                        <div>{deliveryContractLabel(delivery)}</div>
                                                    )}
                                                    {/* Selectable: the dispatcher pastes it into a map when the address is wrong. */}
                                                    <Coordinates>
                                                        {coordinates
                                                            ? `${coordinates.latitude.toFixed(5)}, ${coordinates.longitude.toFixed(5)}`
                                                            : 'Aucune position GPS — arrêt non plaçable sur la carte'}
                                                    </Coordinates>
                                                </ChipDetails>
                                            )}
                                        </ChipBlock>
                                    )}
                                </Draggable>
                            );
                        })}
                        {tasks.map((task, index) => (
                            <Draggable key={task._id} draggableId={task._id} index={deliveries.length + index}>
                                {(dragProvided, dragSnapshot) => (
                                    <ChipBlock
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        {...dragProvided.dragHandleProps}
                                        $dragging={dragSnapshot.isDragging}
                                        $pending={selectionNumberByDeliveryId?.has(task._id) ?? false}
                                        data-test="tour-loose-task"
                                    >
                                        <ChipRow
                                            onClick={
                                                onToggleDelivery
                                                    ? () => onToggleDelivery(task._id)
                                                    : onOpenTask && (() => onOpenTask(task))
                                            }
                                        >
                                            <GripVertical size={13} />
                                            <MapNumber title="Numéro du point sur la carte">
                                                {selectionNumberByDeliveryId?.get(task._id) ??
                                                    mapNumberById.get(task._id) ??
                                                    '·'}
                                            </MapNumber>
                                            <TaskStopContent task={task} />
                                        </ChipRow>
                                    </ChipBlock>
                                )}
                            </Draggable>
                        ))}
                        {deliveries.length === 0 && tasks.length === 0 && (
                            <DropHint>Déposez ici pour retirer l'arrêt de sa tournée</DropHint>
                        )}
                        {dropProvided.placeholder}
                    </List>
                )}
            </Droppable>
        </Panel>
    );
};

const Panel = styled.div`
    background: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.warningColor};
    border-radius: 12px;
    overflow: hidden;
`;

const Head = styled.div`
    padding: 10px 12px;
    font-size: 13px;
    font-weight: 700;
    color: ${({ theme }) => theme.warningColor};
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const Note = styled.div`
    padding: 8px 12px 0;
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const List = styled.div<{ $over: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 12px;
    min-height: 40px;
    border-radius: 10px;
    background: ${({ theme, $over }) => ($over ? theme.backgroundColor : 'transparent')};
    transition: background 0.15s ease;
`;

const DropHint = styled.div`
    border: 1px dashed ${({ theme }) => theme.lightBorderColor};
    border-radius: 8px;
    padding: 12px;
    text-align: center;
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const ChipBlock = styled.div<{ $dragging: boolean; $pending: boolean }>`
    border-radius: 10px;
    background: ${({ theme }) => theme.backgroundColor};
    border: 1px solid ${({ theme, $pending }) => ($pending ? '#f59e0b' : theme.lightBorderColor)};
    color: ${({ theme }) => theme.textColor};
    font-size: 13px;
    cursor: grab;
    box-shadow: ${({ $dragging }) => ($dragging ? '0 12px 28px rgba(0,0,0,0.3)' : 'none')};
`;

const ChipRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 9px;

    > span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex-shrink: 0;
    }
`;

/** The delivery's number on the map — same grey dot, so the eye goes from the chip to the marker and back. */
const MapNumber = styled.span`
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    background: ${UNASSIGNED_COLOR};
`;

const ChipName = styled.span`
    flex: 1;
    min-width: 0;
`;

const ChipDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0 9px 8px 30px;
    font-size: 11px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const Coordinates = styled.span`
    font-variant-numeric: tabular-nums;
    user-select: text;
    cursor: text;
`;
